import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Dirty tracking for the "load a record, edit fields, press Save" form that
 * appears on roughly twenty screens.
 *
 * Settings previously mixed five save models — debounced auto-save, a page-level
 * Save, a dirty-count Save, per-row Saves, and dialog Saves — and only one of
 * them could tell you whether you had unsaved work. This gives all of them the
 * same three answers: what changed, how many, and how to put it back.
 *
 * The re-hydration rule is the part worth reading. React Query refetches on
 * window focus, so a naive `useEffect(() => setDraft(server))` wipes whatever
 * the user typed the moment they alt-tab away and back. Here, incoming server
 * data is adopted only when the form is clean or the identity changed, so a
 * background refetch can never clobber edits in progress.
 */
export interface DirtyForm<T> {
  /** Current edited value. */
  value: T;
  /** Replace the whole value. */
  set: (next: T | ((prev: T) => T)) => void;
  /** Shallow-merge a few fields. */
  patch: (fields: Partial<T>) => void;
  /** The last adopted server value, for per-field comparisons. */
  baseline: T;
  isDirty: boolean;
  /** Keys whose value differs from the baseline. */
  changed: (keyof T)[];
  /** Throw the edits away and go back to the baseline. */
  reset: () => void;
  /** Accept the current value as the new baseline (call after a successful save). */
  commit: (value?: T) => void;
}

export interface UseDirtyFormOptions<T> {
  /**
   * Server value, or `undefined` while loading. Adopted as the baseline when
   * the form is clean or `identity` changed.
   */
  data: T | undefined;
  /**
   * Which record this is. Change it (a different user, role, ticket…) and the
   * form re-hydrates even if it was dirty, because the old edits no longer
   * belong to anything on screen.
   */
  identity?: string | number | null;
  /** Fields to ignore when deciding whether the form is dirty. */
  ignore?: (keyof T)[];
  /** Custom equality, for values holding arrays or nested objects. */
  isEqual?: (a: T[keyof T], b: T[keyof T], key: keyof T) => boolean;
}

export function useDirtyForm<T extends Record<string, unknown>>({
  data,
  identity = null,
  ignore,
  isEqual,
}: UseDirtyFormOptions<T>): DirtyForm<T> | null {
  const [draft, setDraft] = useState<T | null>(null);
  const [baseline, setBaseline] = useState<T | null>(null);
  const hydratedIdentity = useRef<string | number | null | undefined>(undefined);

  const ignoreKeys = useMemo(() => new Set(ignore ?? []), [ignore]);

  const compare = useCallback(
    (a: T, b: T): (keyof T)[] => {
      const keys = new Set([...Object.keys(a), ...Object.keys(b)]) as Set<keyof T>;
      const out: (keyof T)[] = [];
      for (const key of keys) {
        if (ignoreKeys.has(key)) continue;
        const same = isEqual ? isEqual(a[key], b[key], key) : shallowSame(a[key], b[key]);
        if (!same) out.push(key);
      }
      return out;
    },
    [ignoreKeys, isEqual],
  );

  const changed = useMemo(
    () => (draft && baseline ? compare(draft, baseline) : []),
    [draft, baseline, compare],
  );
  const isDirty = changed.length > 0;

  // Kept in a ref so the hydration effect can read dirtiness without listing it
  // as a dependency — depending on it would re-run hydration the instant the
  // form goes clean, which is exactly when a stale payload is still in flight.
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;

  useEffect(() => {
    if (data === undefined) return;
    const identityChanged = hydratedIdentity.current !== identity;
    if (!identityChanged && isDirtyRef.current) return;
    hydratedIdentity.current = identity;
    setBaseline(data);
    setDraft(data);
  }, [data, identity]);

  const set = useCallback((next: T | ((prev: T) => T)) => {
    setDraft((prev) => {
      if (prev == null) return prev;
      return typeof next === 'function' ? (next as (p: T) => T)(prev) : next;
    });
  }, []);

  const patch = useCallback((fields: Partial<T>) => {
    setDraft((prev) => (prev == null ? prev : { ...prev, ...fields }));
  }, []);

  const reset = useCallback(() => {
    setDraft(baseline);
  }, [baseline]);

  const commit = useCallback(
    (value?: T) => {
      const next = value ?? draft;
      if (next == null) return;
      setBaseline(next);
      setDraft(next);
    },
    [draft],
  );

  if (draft == null || baseline == null) return null;

  return { value: draft, set, patch, baseline, isDirty, changed, reset, commit };
}

/**
 * Value equality that handles the two shapes form state actually holds:
 * primitives and flat arrays. Deliberately not a deep compare — a form field
 * holding a deeply nested object should pass its own `isEqual`.
 */
function shallowSame(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((entry, i) => entry === b[i]);
  }
  // Treat null and undefined as the same absence: DRF omits null fields on some
  // endpoints and returns them explicitly on others, and a round-trip through
  // the API should not by itself mark a form dirty.
  if (a == null && b == null) return true;
  return false;
}
